/**
 * useFilterTemplate - Reusable filtering logic from Selected Contractors dashboard.
 * Use with FilterIcon and FilterTemplateDropdown for the full "Filtering Template" experience.
 *
 * @param {Object} options
 * @param {Array<Object>} options.items - Data rows (array of objects)
 * @param {Array<{key: string, label: string, type?: string}>} options.columns - Column definitions (key, label, type: 'text'|'number' for advanced filter)
 * @param {Array<string>} [options.numericColumnKeys] - Column keys that use numeric operators in Advanced tab (default: derived from columns where type==='number')
 * @returns Filter state, handlers, and filtered items
 */
import { useState, useCallback, useMemo } from 'react';

const TEXT_OPERATORS = [
  { value: 'contains', label: 'Contains' },
  { value: 'notContains', label: 'Does not contain' },
  { value: 'equals', label: 'Equals' },
  { value: 'notEquals', label: 'Does not equal' },
  { value: 'startsWith', label: 'Starts with' },
  { value: 'endsWith', label: 'Ends with' },
  { value: 'empty', label: 'Is empty' },
  { value: 'notEmpty', label: 'Is not empty' },
];

const NUMERIC_OPERATORS = [
  { value: 'eq', label: 'Equals (=)' },
  { value: 'neq', label: 'Not equals (≠)' },
  { value: 'gt', label: 'Greater than (>)' },
  { value: 'gte', label: 'Greater or equal (≥)' },
  { value: 'lt', label: 'Less than (<)' },
  { value: 'lte', label: 'Less or equal (≤)' },
];

const DATE_OPERATORS = [
  { value: 'dateEquals', label: 'On date' },
  { value: 'dateBefore', label: 'Before' },
  { value: 'dateAfter', label: 'After' },
  { value: 'dateOnOrBefore', label: 'On or before' },
  { value: 'dateOnOrAfter', label: 'On or after' },
  { value: 'empty', label: 'Is empty' },
  { value: 'notEmpty', label: 'Is not empty' },
];

export { TEXT_OPERATORS, NUMERIC_OPERATORS, DATE_OPERATORS };

export default function useFilterTemplate({ items = [], columns = [], numericColumnKeys: numericKeysProp }) {
  const [columnFilters, setColumnFilters] = useState({});
  const [openFilterColumn, setOpenFilterColumn] = useState(null);
  const [filterSearch, setFilterSearch] = useState('');
  const [filterMode, setFilterMode] = useState({});
  const [filterText, setFilterText] = useState({});
  const [filterButtonPositions, setFilterButtonPositions] = useState({});

  const numericColumnKeys = useMemo(() => {
    if (Array.isArray(numericKeysProp) && numericKeysProp.length > 0) return numericKeysProp;
    return columns.filter(c => c.type === 'number' || c.type === 'percent').map(c => c.key);
  }, [numericKeysProp, columns]);

  const dateColumnKeys = useMemo(() => columns.filter(c => c.type === 'date').map(c => c.key), [columns]);

  const getUniqueValues = useCallback((columnKey) => {
    const values = new Set();
    let hasBlank = false;
    items.forEach(row => {
      const val = row[columnKey];
      if (val === null || val === undefined || val === '') {
        hasBlank = true;
      } else {
        values.add(String(val));
      }
    });
    const sorted = Array.from(values).sort();
    if (hasBlank) sorted.push('(Blank)');
    return sorted;
  }, [items]);

  const handleFilterToggle = useCallback((columnKey, value) => {
    setColumnFilters(prev => {
      const allValues = getUniqueValues(columnKey);
      const current = prev[columnKey];

      if (!current) {
        const newValues = allValues.filter(v => v !== value);
        return { ...prev, [columnKey]: newValues };
      }
      if (current.includes(value)) {
        const newValues = current.filter(v => v !== value);
        if (newValues.length === 0) {
          const { [columnKey]: _, ...rest } = prev;
          return rest;
        }
        return { ...prev, [columnKey]: newValues };
      }
      const newValues = [...current, value];
      if (newValues.length >= allValues.length) {
        const { [columnKey]: _, ...rest } = prev;
        return rest;
      }
      return { ...prev, [columnKey]: newValues };
    });
  }, [getUniqueValues]);

  const clearColumnFilter = useCallback((columnKey) => {
    setColumnFilters(prev => ({ ...prev, [columnKey]: [] }));
    setFilterMode(prev => { const { [columnKey]: _, ...rest } = prev; return rest; });
    setFilterText(prev => { const { [columnKey]: _, ...rest } = prev; return rest; });
  }, []);

  const selectAllColumnFilter = useCallback((columnKey) => {
    setColumnFilters(prev => { const { [columnKey]: _, ...rest } = prev; return rest; });
    setFilterMode(prev => { const { [columnKey]: _, ...rest } = prev; return rest; });
    setFilterText(prev => { const { [columnKey]: _, ...rest } = prev; return rest; });
  }, []);

  const applyTextFilter = useCallback((columnKey, mode, text) => {
    const isEmptyMode = ['empty', 'notEmpty'].includes(mode);
    if (!text && !isEmptyMode) {
      setFilterMode(prev => { const { [columnKey]: _, ...rest } = prev; return rest; });
      setFilterText(prev => { const { [columnKey]: _, ...rest } = prev; return rest; });
      setColumnFilters(prev => { const { [columnKey]: _, ...rest } = prev; return rest; });
    } else {
      setFilterMode(prev => ({ ...prev, [columnKey]: mode }));
      setFilterText(prev => ({ ...prev, [columnKey]: isEmptyMode ? '' : text }));
      setColumnFilters(prev => { const { [columnKey]: _, ...rest } = prev; return rest; });
    }
    setOpenFilterColumn(null);
    setFilterSearch('');
  }, []);

  const clearAllFilters = useCallback((optionalCallback) => {
    setColumnFilters({});
    setFilterMode({});
    setFilterText({});
    setFilterSearch('');
    setOpenFilterColumn(null);
    if (typeof optionalCallback === 'function') optionalCallback();
  }, []);

  const getActiveFilters = useCallback(() => {
    const active = [];
    Object.entries(columnFilters).forEach(([key, values]) => {
      if (values && values.length > 0) {
        active.push({ key, type: 'values', count: values.length });
      }
    });
    Object.entries(filterMode).forEach(([key, mode]) => {
      if (mode && filterText[key] !== undefined) {
        active.push({ key, type: 'text', mode, value: filterText[key] });
      }
    });
    return active;
  }, [columnFilters, filterMode, filterText]);

  const openFilterAtPosition = useCallback((e, columnKey) => {
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    setFilterButtonPositions(prev => ({
      ...prev,
      [columnKey]: { top: rect.bottom + 5, left: rect.left },
    }));
    setFilterSearch('');
    setOpenFilterColumn(prev => (prev === columnKey ? null : columnKey));
  }, []);

  const filteredItems = useMemo(() => {
    let result = [...items];

    Object.entries(columnFilters).forEach(([key, values]) => {
      if (values && values.length > 0) {
        result = result.filter(item => {
          const val = item[key];
          const isBlank = val === null || val === undefined || val === '';
          if (isBlank) return values.includes('(Blank)');
          return values.includes(String(val));
        });
      }
    });

    Object.entries(filterMode).forEach(([key, mode]) => {
      const text = filterText[key] || '';
      const isNumericOperator = ['eq', 'neq', 'gt', 'gte', 'lt', 'lte'].includes(mode);
      const isDateOperator = ['dateEquals', 'dateBefore', 'dateAfter', 'dateOnOrBefore', 'dateOnOrAfter'].includes(mode);
      if (!text && !['empty', 'notEmpty'].includes(mode)) return;

      result = result.filter(item => {
        const val = item[key];
        if (isDateOperator) {
          if (!val) return false;
          const d = new Date(val); const fd = new Date(text);
          if (isNaN(d.getTime()) || isNaN(fd.getTime())) return true;
          const dDay = new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
          const fDay = new Date(fd.getFullYear(), fd.getMonth(), fd.getDate()).getTime();
          switch (mode) {
            case 'dateEquals': return dDay === fDay;
            case 'dateBefore': return dDay < fDay;
            case 'dateAfter': return dDay > fDay;
            case 'dateOnOrBefore': return dDay <= fDay;
            case 'dateOnOrAfter': return dDay >= fDay;
            default: return true;
          }
        }
        if (isNumericOperator) {
          const numVal = parseFloat(val);
          const filterNum = parseFloat(text);
          if (isNaN(numVal)) return mode === 'neq';
          if (isNaN(filterNum)) return true;
          switch (mode) {
            case 'eq': return numVal === filterNum;
            case 'neq': return numVal !== filterNum;
            case 'gt': return numVal > filterNum;
            case 'gte': return numVal >= filterNum;
            case 'lt': return numVal < filterNum;
            case 'lte': return numVal <= filterNum;
            default: return true;
          }
        }
        const strVal = val !== null && val !== undefined ? String(val).toLowerCase() : '';
        const textLower = text.toLowerCase();
        switch (mode) {
          case 'equals': return strVal === textLower;
          case 'notEquals': return strVal !== textLower;
          case 'contains': return strVal.includes(textLower);
          case 'notContains': return !strVal.includes(textLower);
          case 'startsWith': return strVal.startsWith(textLower);
          case 'endsWith': return strVal.endsWith(textLower);
          case 'empty': return !strVal;
          case 'notEmpty': return !!strVal;
          default: return true;
        }
      });
    });

    return result;
  }, [items, columnFilters, filterMode, filterText]);

  const columnLabels = useMemo(() => {
    const map = {};
    columns.forEach(c => { map[c.key] = c.label || c.key; });
    return map;
  }, [columns]);

  return {
    filteredItems,
    columnFilters,
    openFilterColumn,
    filterSearch,
    filterMode,
    filterText,
    filterButtonPositions,
    setOpenFilterColumn,
    setFilterSearch,
    setFilterButtonPositions,
    getUniqueValues,
    handleFilterToggle,
    clearColumnFilter,
    selectAllColumnFilter,
    applyTextFilter,
    clearAllFilters,
    getActiveFilters,
    openFilterAtPosition,
    numericColumnKeys,
    dateColumnKeys,
    columnLabels,
  };
}
